<template>
  <MDBModal v-model="showGeneralModal" tabindex="-1" centered size="xl" @hide="closeModal"
    class="bg-modal extra-modal-level">
    <form v-if="attributes" class="m-5" @submit.prevent="handleSubmit">
      <div class="d-flex flex-wrap gap-5">
        <div v-for="(group, groupKey) in attributes" :key="groupKey">
          <h5 class="mt-2 text-uppercase" v-if="Object.keys(group).length">{{
            t(`generateReport.${groupKey}.title`) }}
          </h5>
          <div :class="groupHasOptions(group) ? 'd-flex flex-wrap gap-4' : ''">
            <div v-for="(attribute, attrKey) in group" :key="attrKey" class="min-w">
              <div v-if="attribute.options?.length">
                <strong class="d-block mt-2 mb-1">
                  {{ t(`generateReport.${groupKey}.${attrKey}.title`, attrKey) }}
                </strong>
                <div class="height">
                  <div v-for="option in attribute.options" :key="option.subattribute_id">
                    <input :type="attribute.attribute_type ?? 'checkbox'" class="form-check-input me-2"
                      :value="option.subattribute_id"
                      :checked="isSelected(attribute.attribute_id, option.subattribute_id)"
                      @change="toggleOption(attribute.attribute_id, option.subattribute_id, attribute.attribute_type)"
                      :id="'checkbox-' + attribute.attribute_id + '-' + option.subattribute_id"
                      :disabled="groupKey === 'meta' && !isCalculatedDataSelected" />
                    <label class="form-check-label mb-0"
                      :for="'checkbox-' + attribute.attribute_id + '-' + option.subattribute_id">
                      {{ te(`generateReport.${groupKey}.${attrKey}.${option.label}`)
                        ? t(`generateReport.${groupKey}.${attrKey}.${option.label}`)
                        : option.label }}
                    </label>
                  </div>
                </div>
                <div class="form-check mt-1 mb-1" v-if="!attribute.attribute_type">
                  <input :type="attribute.attribute_type ?? 'checkbox'" class="form-check-input me-2"
                    :checked="areAllSelected(attribute.attribute_id, attribute.options, attribute.attribute_type)"
                    @change="toggleAll(attribute.attribute_id, attribute.options, attribute.attribute_type)"
                    :id="'selectAll-' + attribute.attribute_id"
                    :disabled="groupKey === 'meta' && !isCalculatedDataSelected" />
                  <label class="form-check-label font-semibold" :for="'selectAll-' + attribute.attribute_id">
                    {{ t("generateReport.selectAll") }}
                  </label>
                </div>
              </div>

              <div v-else class="form-check">
                <input :type="attribute.attribute_type ?? 'checkbox'" class="form-check-input me-2"
                  :checked="isSelected(attribute.attribute_id)"
                  @change="toggleOption(attribute.attribute_id, null, attribute.attribute_type)"
                  :id="'checkbox-' + attribute.attribute_id" />
                <label class="form-check-label mb-0" :for="'checkbox-' + attribute.attribute_id">
                  {{ t(`generateReport.${groupKey}.${attrKey}`, attrKey) }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="errorMessage" class="text-danger mt-2">
        {{ errorMessage }}
      </div>
      <div class="d-flex justify-content-between gap-2">
        <button class="mt-4 btn-outline-info" :text="t('generateReport.reset')" @click="resetFilters">{{
          t('generateReport.reset') }}</button>

        <div v-if="loading" class="mt-4 btn-outline-warning p-3">
          {{ t('generateReport.loadingInfo') }}
        </div>

        <button class="mt-4 btn btn-primary btn-lg d-flex align-items-center overflow-hidden" type="submit"
          :disabled="loading">
          {{ loading ? t('global.buttons.loading') : t('generateReport.generate') }}
          <div class="loader-margin">
            <Loader class="-mt-2" :small="true" spinner="spinner.svg" :show="loading" />
          </div>
        </button>
      </div>
    </form>
  </MDBModal>
  <button type="button" @click="openModal"
    class="download-attribute-trigger btn btn-danger text-white text-uppercase px-5" :class="buttonClass"
    :disabled="loading">
    {{ buttonLabel }}
  </button>
</template>

<script setup>
import { ref, computed, watch, defineProps } from 'vue';
import { DownloadService } from '@/services/downloadAttributes.service.js';
import { MDBModal } from 'mdb-vue-ui-kit';
import Loader from '@/components/General/Loader';
import mockedAttributesSales from '@/data/old-tenants/nl/download-attributes-sales.json';
import mockedAttributesWholesalers from '@/data/old-tenants/nl/download-attributes.json';

import { useI18n } from 'vue-i18n';
import { useStore } from "vuex";
import { useTenantConfig } from "@/composables/useTenantConfig";
import { userCanSeeKickbackList } from "@/composables/useProfileFieldCheck";
const { t, te } = useI18n();
const store = useStore();
const { tenantConfig } = useTenantConfig();
const { showKickbackFeeList } = userCanSeeKickbackList(tenantConfig);
const props = defineProps({
  buttonLabel: {
    type: String,
    default: "Generate report",
  },
  buttonClass: {
    type: String,
    default: "",
  },
});

const loading = ref(false);
const showGeneralModal = ref(false);

const errorMessage = ref("")
const buttonLabel = computed(() => props.buttonLabel);
const buttonClass = computed(() => props.buttonClass);

const user = computed(() => store.getters.getMe);
const validateDistributor = () => {
  const distributor = attributes.value?.sales_structure?.distributor
  if (!distributor?.options?.length) return true

  const sel = selected.value[distributor.attribute_id]
  return Array.isArray(sel) && sel.length > 0
}

const closeModal = () => {
  showGeneralModal.value = false;
};

const normalizeApiAttributes = (data) => {
  if (!data || typeof data !== "object") return {};

  const normalized = { ...data };
  const hasFlatSalesStructure =
    normalized.sales_representative || normalized.distributor;

  if (hasFlatSalesStructure) {
    normalized.sales_structure = {
      ...(normalized.sales_structure || {}),
      ...(normalized.sales_representative
        ? { sales_representative: normalized.sales_representative }
        : {}),
      ...(normalized.distributor ? { distributor: normalized.distributor } : {}),
    };
    delete normalized.sales_representative;
    delete normalized.distributor;
  }

  return normalized;
};

const openModal = async () => {
  loading.value = true;
  const roleNames = user.value?.roles?.map((item) => item.name) || [];
  const isWholesalerLike = roleNames.some((role) =>
    ["wholesaler", "wholesalerStaff"].includes(role)
  );

  const mockedBaseAttributes = isWholesalerLike
    ? mockedAttributesWholesalers
    : mockedAttributesSales;

  try {
    const responseData = await DownloadService.getAttributes();
    const payload =
      responseData && typeof responseData === "object" && "status" in responseData
        ? responseData.data
        : responseData;
    const normalizedData = normalizeApiAttributes(payload);

    attributes.value = cleanAttributes({
      ...mockedBaseAttributes,
      ...normalizedData,
      sales_structure: {
        ...(mockedBaseAttributes.sales_structure || {}),
        ...(normalizedData.sales_structure || {}),
      },
    });
  } catch (e) {
    console.error("Error loading report attributes", e);
    attributes.value = cleanAttributes({ ...mockedBaseAttributes });
  }

  showGeneralModal.value = true;
  loading.value = false;
};

const attributes = ref(null);
const selected = ref({});

const resetFilters = () => {
  selected.value = {};
};

const isSelected = (attrId, subId = null) => {
  if (subId !== null) {
    return selected.value[attrId]?.includes(subId);
  }
  return selected.value[attrId] === true;
};

const areAllSelected = (attrId, options, type = 'checkbox') => {
  if (type === 'radio') return false;
  const selectedOptions = selected.value[attrId] || [];
  return options.every(opt => selectedOptions.includes(opt.subattribute_id));
};

const toggleAll = (attrId, options, type = 'checkbox') => {
  if (type === 'radio') return;
  if (areAllSelected(attrId, options, 'checkbox')) {
    delete selected.value[attrId];
  } else {
    selected.value[attrId] = options.map(opt => opt.subattribute_id);
  }
};

const toggleOption = (attrId, subId = null, type = 'checkbox') => {
  if (subId !== null) {
    if (type === 'radio') {
      selected.value[attrId] = [subId];
    } else {
      if (!selected.value[attrId]) selected.value[attrId] = [];
      const index = selected.value[attrId].indexOf(subId);
      if (index > -1) {
        selected.value[attrId].splice(index, 1);
        if (selected.value[attrId].length === 0) delete selected.value[attrId];
      } else {
        selected.value[attrId].push(subId);
      }
    }
  } else {
    selected.value[attrId] = !selected.value[attrId];
    if (!selected.value[attrId]) delete selected.value[attrId];
  }

  if (errorMessage.value && validateDistributor()) {
    errorMessage.value = "";
  }
};

const buildPayload = () => {
  const payload = { format: 'xls' };

  for (const [groupKey, group] of Object.entries(attributes.value)) {
    payload[groupKey] = {};

    for (const [attrKey, attribute] of Object.entries(group)) {
      const attrId = attribute.attribute_id;
      const isChecked = Object.prototype.hasOwnProperty.call(selected.value, attrId);

      if (!isChecked) continue;

      const options = attribute.options || null;

      if (options) {
        const selectedOptions = selected.value[attrId] || [];
        const filteredOptions = options.filter(o => selectedOptions.includes(o.subattribute_id));
        payload[groupKey][attrKey] = {
          attribute_id: attrId,
          options: filteredOptions.map(opt => ({
            subattribute_id: opt.subattribute_id,
            label: opt.label
          }))
        };
      } else {
        payload[groupKey][attrKey] = {
          attribute_id: attrId,
          options: null
        };
      }
    }

    if (Object.keys(payload[groupKey]).length === 0) {
      delete payload[groupKey];
    }
  }

  return payload;
};

const handleSubmit = async () => {
  try {
    errorMessage.value = ""
    if (!validateDistributor()) {
      errorMessage.value = t("generateReport.validation.distributorRequired")
      return
    }

    loading.value = true;
    const payload = buildPayload();
    const response = await DownloadService.downloadReport(payload);

    if (!response || response.status !== 200) {
      console.error('Download failed', response);
      return;
    }

    // Nazwa pliku z nagłówka (jeśli backend ją poda)
    let filename = `report-${new Date().toISOString().slice(0, 10)}.xls`;
    const dispo = response.headers?.['content-disposition'] || response.headers?.get?.('content-disposition');
    if (dispo) {
      const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(dispo);
      if (m) filename = decodeURIComponent(m[1]);
    }

    // Blob z odpowiedzi
    const blob = new Blob([response.data], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    // „Prawdziwy” download bez nawigacji
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.download = filename;       // kluczowe — bez tego przeglądarka może wejść na blob:
    link.target = '_self';
    document.body.appendChild(link);
    link.click();
    link.remove();

    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) {
    console.error('Error applying filters:', err);
  } finally {
    loading.value = false;
  }
};

const groupHasOptions = (group) => {
  return Object.values(group).some(attribute => attribute.options?.length);
};

const isCalculatedDataSelected = computed(() => {
  const calculated = attributes.value?.calculated_data;
  return calculated
    ? Object.values(calculated).some(attr => {
      const sel = selected.value[attr.attribute_id];
      return Array.isArray(sel) ? sel.length > 0 : !!sel;
    })
    : false;
});

watch(isCalculatedDataSelected, () => {
  const metaGroup = attributes.value?.meta;
  if (!metaGroup) return;

  Object.values(metaGroup).forEach(attribute => {
    delete selected.value[attribute.attribute_id];
  });
});

function cleanAttributes(obj) {
  if (!obj) return obj;

  let result = { ...obj };

  if (result.sales_structure) {
    const sales_structure = Object.fromEntries(
      Object.entries(result.sales_structure).filter(([, value]) => value?.options?.length)
    );
    result = { ...result, sales_structure: sales_structure || {} };
  }

  if (!showKickbackFeeList() && result.calculated_data?.bonus) {
    const calculated_data = { ...result.calculated_data };
    delete calculated_data.bonus;
    result = { ...result, calculated_data };
  }

  return result;
}
</script>

<style scoped lang="scss">
@import "@/styles/_variables.scss";

.download-attribute-trigger.btn-danger {
  background-color: $dangerRed;
  border-color: $dangerRed;
  color: $backgroundWhite;

  &:hover,
  &:focus,
  &:active {
    background-color: darken($dangerRed, 5%);
    border-color: darken($dangerRed, 5%);
    color: $backgroundWhite;
  }
}

.height {
  max-height: 400px;
  overflow-y: auto;
}

.loader-margin {
  margin-top: -12px;
  height: 40px;
}

.min-w {
  min-width: 200px;
  max-height: 400px;
  overflow-y: auto;
}

.loader-margin {
  margin-top: -12px;
  height: 40px;
}

.min-w {
  min-width: 200px;
}
</style>
